// Single source of truth for whether a project is usable as the active working
// project. A project is "inactive" if it has been archived or its end date has
// passed. The backend intentionally still returns these projects (a read must
// never delete data), so the client decides how to present and gate them.

export interface ProjectStatusLike {
  status?: string;
  expire_date?: string | null;
}

export type ProjectInactiveReason = 'archived' | 'expired' | null;

export function projectInactiveReason(project: ProjectStatusLike | null | undefined): ProjectInactiveReason {
  if (!project) return null;
  if (project.status === 'archived') return 'archived';
  if (project.expire_date) {
    const exp = new Date(project.expire_date);
    if (!isNaN(exp.getTime())) {
      // Compare on the calendar day — a project is expired only once its end
      // date is fully in the past, not at the first instant of that day.
      const endOfExpiry = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate(), 23, 59, 59, 999);
      if (endOfExpiry.getTime() < Date.now()) return 'expired';
    }
  }
  return null;
}

export function isProjectInactive(project: ProjectStatusLike | null | undefined): boolean {
  return projectInactiveReason(project) !== null;
}

export function projectInactiveLabel(reason: ProjectInactiveReason): string {
  if (reason === 'archived') return 'Archived';
  if (reason === 'expired') return 'Expired';
  return '';
}
